import React, { useState } from "react";
import PageHero from "../components/PageHero";
import { EditableText, EditableImage } from "../components/live/Editable";
import { useToast } from "../hooks/use-toast";
import { CheckCircle2, Loader2 } from "lucide-react";
import api from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";

const QUALIFICATIONS = [
  "Degree Holder/ Currently studying",
  "Diploma Passed/ Currently studying",
  "ITI Passed/ Currently studying",
  "12 Pass",
  "11 Pass",
  "10 Pass",
  "Other",
];

const COURSE_OPTIONS = [
  "Tool & Die Making",
  "Precision Machinist",
  "Mechatronics",
  "Diploma in Tool Design",
  "Post Diploma in Automation",
];

const STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chandigarh", "Chhattisgarh",
  "Dadra and Nagar Haveli", "Daman and Diu", "Delhi", "Goa", "Gujarat", "Haryana",
  "Himachal Pradesh", "Jammu and Kashmir", "Jharkhand", "Karnataka", "Kerala", "Lakshadweep",
  "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha",
  "Puducherry", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura",
  "Uttarakhand", "Uttar Pradesh", "West Bengal",
];

export default function AdmissionsPage() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    qualification: "",
    course_interest: "",
    address: "",
    state: "",
    city: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const { toast } = useToast();

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/admissions", form);
      setDone(true);
      toast({ title: "Application submitted!", description: "Our team will contact you soon." });
      setForm({ full_name: "", email: "", phone: "", qualification: "", course_interest: "", address: "", state: "", city: "", message: "" });
    } catch (err) {
      toast({ title: "Submission failed", description: err?.response?.data?.detail || "Try again later", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHero path="admissions.hero" kicker="Admissions Open 2026" title="Admissions" subtitle="Fill in your details to learn more about our technical training courses. Our admissions team will reach out." />
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2">
            <EditableImage
              path="admissions.main.image"
              src="/assets/kiki/admission-main.jpg"
              alt="Admissions open at Kiki Centre"
              className="rounded-2xl shadow-lg overflow-hidden mb-7"
              imgClassName="w-full h-full object-cover"
              data-testid="admissions-main-image"
            />
            <EditableText path="admissions.why.title" as="h2" className="text-2xl font-bold text-[#0B2A6B] mb-4 block">Why apply to Kiki?</EditableText>
            <ul className="space-y-4">
              {[
                "3.5 years German Technical Training Course",
                "For 10th/12th pass students (all streams)",
                "Stipend from Year 2 onwards",
                "German HWK Koblenz Certification",
                "100% placement track record",
                "World-class faculty and facilities",
              ].map((t, i) => (
                <li key={t} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-yellow-500 mt-0.5" />
                  <EditableText path={`admissions.why.${i}`} as="span" className="text-slate-700">{t}</EditableText>
                </li>
              ))}
            </ul>
            <div className="mt-7 rounded-2xl bg-[#0B2A6B] text-white p-6">
              <EditableText path="admissions.call.text" as="p" multiline className="text-sm text-slate-300 mb-1 block">Call for assistance or to enquire about our 3½ year technical courses</EditableText>
              <a href="tel:+918800288994" className="text-xl font-bold text-yellow-400" data-testid="admissions-call-link">
                8800288994
              </a>
            </div>
          </div>

          <div className="lg:col-span-3">
            {done ? (
              <div className="bg-yellow-50 border-2 border-yellow-300 rounded-2xl p-10 text-center">
                <CheckCircle2 className="h-14 w-14 text-yellow-500 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-[#0B2A6B] mb-2">Application Received!</h3>
                <p className="text-slate-600 mb-6">Our admissions team will contact you within 24-48 hours.</p>
                <Button onClick={() => setDone(false)} className="bg-[#0B2A6B] hover:bg-[#061a47] text-white">Submit Another</Button>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-white rounded-2xl shadow-sm border border-slate-100 p-7 space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Name *"><Input required value={form.full_name} onChange={(e) => update("full_name", e.target.value)} placeholder="Your full name" data-testid="admissions-name-input" /></Field>
                  <Field label="Email *"><Input type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@email.com" data-testid="admissions-email-input" /></Field>
                  <Field label="Phone *"><Input required value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+91..." data-testid="admissions-phone-input" /></Field>
                  <Field label="Qualification *">
                    <Select value={form.qualification} onValueChange={(v) => update("qualification", v)} required>
                      <SelectTrigger data-testid="admissions-qualification-select"><SelectValue placeholder="Please Select" /></SelectTrigger>
                      <SelectContent>
                        {QUALIFICATIONS.map((q) => (
                          <SelectItem key={q} value={q}>{q}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Course Applied For *">
                    <Select value={form.course_interest} onValueChange={(v) => update("course_interest", v)} required>
                      <SelectTrigger data-testid="admissions-course-select"><SelectValue placeholder="Please Select" /></SelectTrigger>
                      <SelectContent>
                        {COURSE_OPTIONS.map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Address *"><Input required value={form.address} onChange={(e) => update("address", e.target.value)} placeholder="Your address" data-testid="admissions-address-input" /></Field>
                  <Field label="State *">
                    <Select value={form.state} onValueChange={(v) => update("state", v)} required>
                      <SelectTrigger data-testid="admissions-state-select"><SelectValue placeholder="Please Select" /></SelectTrigger>
                      <SelectContent>
                        {STATES.map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="City *"><Input required value={form.city} onChange={(e) => update("city", e.target.value)} placeholder="Your city" data-testid="admissions-city-input" /></Field>
                </div>
                <Field label="Your Message"><Textarea rows={4} value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Tell us about yourself..." /></Field>
                <Button type="submit" disabled={loading} className="w-full bg-yellow-400 hover:bg-yellow-500 text-[#0B2A6B] font-bold h-12 text-base">
                  {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Submitting...</> : "Submit Application"}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-sm font-semibold text-slate-700 mb-1.5 inline-block">{label}</Label>
      {children}
    </div>
  );
}
